import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Send,
  Radar,
  UserRound,
  Zap,
  Crown,
  MailCheck,
  Reply,
  CalendarClock,
  ArrowRight,
  Database,
} from "lucide-react";

const STATUS_COLOR = {
  sent: "text-amber-400 bg-orange-500/10 border-orange-500/30",
  opened: "text-orange-300 bg-orange-500/10 border-orange-500/30",
  replied: "text-emerald-300 bg-emerald-500/10 border-emerald-500/30",
  interview: "text-amber-300 bg-amber-500/10 border-amber-500/30",
};

function StatCard({ icon: Icon, label, value, hint, delay }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay }}
      className="rounded-2xl border border-slate-200 bg-white p-5"
    >
      <div className="flex items-center justify-between">
        <span className="text-xs font-mono uppercase tracking-widest text-slate-500">{label}</span>
        <Icon className="w-4 h-4 text-amber-500" />
      </div>
      <div className="text-3xl font-bold mt-3 font-display">{value}</div>
      {hint && <p className="text-xs text-slate-500 mt-1">{hint}</p>}
    </motion.div>
  );
}

export default function Overview() {
  const { user, quota, openUpgrade } = useAuth();
  const [apps, setApps] = useState([]);
  const [replyQuota, setReplyQuota] = useState(null);

  useEffect(() => {
    api.get("/applications").then(({ data }) => setApps(data.applications)).catch(() => {});
    api.get("/communications/quota").then(({ data }) => setReplyQuota(data)).catch(() => {});
  }, []);

  const replied = apps.filter((a) => ["replied", "interview"].includes(a.status)).length;
  const interviews = apps.filter((a) => a.status === "interview").length;
  const appsText = quota?.is_pro ? "Unlimited applications" : `${quota?.remaining ?? 0} applications left`;
  const replyText = replyQuota ? (replyQuota.unlimited ? "Unlimited replies" : `${replyQuota.remaining} replies left`) : null;
  const planHint = replyText ? `${appsText} · ${replyText}` : appsText;

  return (
    <div className="max-w-6xl mx-auto space-y-8" data-testid="overview-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight font-display">
          Welcome back{user?.name ? `, ${user.name.split(" ")[0]}` : ""}.
        </h1>
        <p className="text-slate-500 mt-1">Here's your outreach at a glance.</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={Send} label="Applications" value={apps.length} hint="Total dispatched" delay={0} />
        <StatCard icon={Reply} label="Responses" value={replied} hint="Replied or interview" delay={0.06} />
        <StatCard icon={CalendarClock} label="Interviews" value={interviews} hint="Scheduled" delay={0.12} />
        <StatCard
          icon={quota?.is_pro ? Crown : Zap}
          label="Plan"
          value={quota?.is_pro ? "Pro" : `${quota?.remaining ?? 0} left`}
          hint={planHint}
          delay={0.18}
        />
      </div>

      {/* Quick actions */}
      <div className="grid lg:grid-cols-12 gap-6">
        <div className="lg:col-span-8 rounded-2xl border border-slate-200 bg-white p-6 sm:p-8">
          <h2 className="text-lg font-semibold">Quick actions</h2>
          <div className="grid sm:grid-cols-2 gap-4 mt-5">
            <Link to="/app/discover">
              <div className="group rounded-xl border border-slate-200 hover:border-orange-500/40 bg-slate-50 p-5 transition-colors cursor-pointer h-full">
                <Radar className="w-6 h-6 text-amber-500" />
                <h3 className="font-semibold mt-3 flex items-center gap-1">
                  Discover & Apply
                  <ArrowRight className="w-4 h-4 opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all" />
                </h3>
                <p className="text-sm text-slate-500 mt-1">Find recruiter emails and send a tailored pitch.</p>
              </div>
            </Link>
            <Link to="/app/profile">
              <div className="group rounded-xl border border-slate-200 hover:border-orange-500/40 bg-slate-50 p-5 transition-colors cursor-pointer h-full">
                <UserRound className="w-6 h-6 text-orange-400" />
                <h3 className="font-semibold mt-3 flex items-center gap-1">
                  Update profile
                  <ArrowRight className="w-4 h-4 opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all" />
                </h3>
                <p className="text-sm text-slate-500 mt-1">Upload your resume so pitches stay on point.</p>
              </div>
            </Link>
          </div>
        </div>

        <div className="lg:col-span-4 space-y-6">
          {!quota?.is_pro && (
            <div className="rounded-2xl border border-orange-500/30 bg-gradient-to-br from-orange-500/10 to-orange-500/5 p-6">
              <Crown className="w-6 h-6 text-amber-500" />
              <h3 className="font-semibold mt-3">Upgrade to Pro</h3>
              <p className="text-sm text-slate-500 mt-1">Unlimited applications and priority AI pitches.</p>
              <Button
                onClick={openUpgrade}
                data-testid="overview-upgrade-button"
                className="w-full mt-4 bg-amber-500 text-black hover:bg-amber-400 font-semibold transition-colors"
              >
                Go Pro
              </Button>
            </div>
          )}
          <div className="rounded-2xl border border-slate-200 bg-white p-6">
            <div className="flex items-center gap-2 text-emerald-400">
              <Database className="w-5 h-5" />
              <span className="text-sm font-semibold">Shared Profile Sync</span>
            </div>
            <p className="text-xs text-slate-500 mt-2 leading-relaxed">
              Your profile lives in the shared recruitment database — resume data carries across apps, no re-uploading.
            </p>
          </div>
        </div>
      </div>

      {/* Recent */}
      <div className="rounded-2xl border border-slate-200 bg-white p-6 sm:p-8">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">Recent applications</h2>
          <Link to="/app/applications" className="text-sm text-amber-500 hover:text-amber-400 transition-colors">
            View all
          </Link>
        </div>
        {apps.length === 0 ? (
          <div className="text-center py-12 text-slate-500">
            <MailCheck className="w-10 h-10 mx-auto opacity-40" />
            <p className="mt-3 text-sm">No applications yet. Head to Discover & Apply to send your first one.</p>
          </div>
        ) : (
          <div className="mt-5 space-y-3">
            {apps.slice(0, 5).map((a) => (
              <div key={a.id} className="flex items-center justify-between gap-4 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
                <div className="min-w-0">
                  <p className="font-medium truncate">{a.job_title || "Application"} · {a.company}</p>
                  <p className="text-xs text-slate-500 truncate font-mono">{a.recruiter_email}</p>
                </div>
                <span className={`shrink-0 text-xs px-2.5 py-1 rounded-full border capitalize ${STATUS_COLOR[a.status] || STATUS_COLOR.sent}`}>
                  {a.status}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
